// @ts-nocheck
/**
 * Manage Webhooks list — redesigned to obd3-gui pattern
 * (FillPage + PageHeader + DataTable + DropdownMenu row actions +
 * ConfirmDialog + JSON viewer Dialog).
 * Event map preserved: 1 HANGUP, 2 DTMF, 3 CONNECTED_CALLS.
 */
import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import CopyButton from '@/features/api-webhook/components/CopyButton';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_WEBHOOK_LIST, API_WEBHOOK_DELETE } from '@/service/url';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { ROUTES, USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

interface JsonModal {
  title: string;
  content: string;
}

const getEventName = (eventId: any) => {
  if (eventId == 1) return 'HANGUP';
  if (eventId == 2) return 'DTMF';
  if (eventId == 3) return 'CONNECTED_CALLS';
  return 'UNKNOWN';
};

const formatTimeContent = (row: any, field = 'created_at') => {
  const value = row[field];
  if (!value) return null;
  return new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

const eventToneClass = (name: string) => {
  switch (name) {
    case 'HANGUP':
      return 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
    case 'DTMF':
      return 'bg-sky-500/15 text-sky-700 dark:text-sky-400';
    case 'CONNECTED_CALLS':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const ManageWebhookList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [webhooksData, setWebhooksData] = useState<any[]>([]);
  const [jsonModal, setJsonModal] = useState<JsonModal | null>(null);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(
        parseModuleOperations(user.acl_settings.module_operations),
      );
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  useEffect(() => {
    setLoading(true);
    const user = getUser();
    if (user?.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    fetchList();
  }, []);

  const fetchList = () => {
    getApiCall(API_WEBHOOK_LIST, {})
      .then((resp: any) => setWebhooksData(resp || []))
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, null, null);
          toast.error(friendlyApiError(err) || 'Failed to load webhooks');
        } else {
          console.log(err);
        }
      })
      .finally(() => setLoading(false));
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setDeleting(true);
    postApiCall(API_WEBHOOK_DELETE, { id: pendingDelete.id })
      .then(() => {
        toast.success('Webhook deleted successfully');
        setPendingDelete(null);
        fetchList();
      })
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Failed to delete webhook'))
      .finally(() => setDeleting(false));
  };

  const openJson = (content: any, title: string) => {
    try {
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      setJsonModal({ title, content: JSON.stringify(parsed, null, 2) });
    } catch {
      setJsonModal({ title, content: content || '{}' });
    }
  };

  const formattedData = useMemo(
    () =>
      webhooksData.map((item: any) => ({
        ...item,
        event_name: getEventName(item.event_id),
      })),
    [webhooksData],
  );

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: 'webhook-list',
        data: formattedData,
        columns: [
          { header: 'Webhook Id', accessor: 'id' },
          { header: 'Webhook Name', accessor: (row: any) => row.webhook_name || '-' },
          { header: 'Webhook Url', accessor: (row: any) => row.url || '-' },
          {
            header: 'Webhook Event',
            accessor: (row: any) =>
              Array.isArray(row.event_name) ? row.event_name.join(', ') : row.event_name || '-',
          },
          { header: 'Created At', accessor: (row: any) => formatTimeContent(row, 'created_at') },
          { header: 'Updated At', accessor: (row: any) => formatTimeContent(row, 'updated_at') },
          {
            header: 'Headers',
            accessor: (row: any) => (row.header_json ? JSON.stringify(row.header_json) : '-'),
          },
          {
            header: 'Body',
            accessor: (row: any) => (row.body_json ? JSON.stringify(row.body_json) : '-'),
          },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const isCallCentreAdmin = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.WEBHOOK,
    'read',
  );
  const canAdd =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.WEBHOOK, 'add');
  const canEdit =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.WEBHOOK, 'edit');
  const canDelete =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.WEBHOOK, 'delete');
  const showActions = canEdit || canDelete;

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
        ),
      },
      {
        accessorKey: 'webhook_name',
        header: 'Name',
        cell: ({ row }) =>
          canEdit ? (
            <Link
              to={`/${ROUTES.MANAGE_WEBHOOK.EDIT.replace(':id', row.original.id)}`}
              className="font-medium text-foreground hover:underline"
            >
              {row.original.webhook_name || '—'}
            </Link>
          ) : (
            <span className="font-medium text-foreground">
              {row.original.webhook_name || '—'}
            </span>
          ),
      },
      {
        accessorKey: 'url',
        header: 'URL',
        size: 100,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.url ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openJson(row.original.url, 'URL')}
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'event_name',
        header: 'Event',
        size: 160,
        cell: ({ row }) => {
          const name = getEventName(row.original.event_id);
          return (
            <span
              className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${eventToneClass(
                name,
              )}`}
            >
              {name}
            </span>
          );
        },
      },
      {
        id: 'header_json',
        header: 'Headers',
        size: 100,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.header_json ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openJson(row.original.header_json, 'Headers')}
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'body_json',
        header: 'Body',
        size: 100,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.body_json ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openJson(row.original.body_json, 'Body')}
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatTimeContent(row.original, 'created_at') || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatTimeContent(row.original, 'updated_at') || '—'}
          </span>
        ),
      },
    ];

    if (showActions) {
      cols.push({
        id: 'actions',
        header: 'Action',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            {canEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Edit webhook"
                onClick={() =>
                  navigate(`/${ROUTES.MANAGE_WEBHOOK.EDIT.replace(':id', row.original.id)}`)
                }
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                aria-label="Delete webhook"
                onClick={() => setPendingDelete(row.original)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [showActions, canEdit, canDelete, isSuperAdmin, loggedInUserACL, navigate]);

  if (loading) return <Loader />;

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="Webhooks" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }


  return (
    <FillPage>
      <PageHeader
        title="Webhooks"
        description="Callbacks fired on call events — HANGUP, DTMF, and CONNECTED_CALLS."
        actions={
          <>
            {webhooksData.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={exporting}>
                Export
              </Button>
            )}
            {canAdd && (
              <Button asChild>
                <Link to={`/${ROUTES.MANAGE_WEBHOOK.CREATE}`}>
                  <Plus className="h-4 w-4" />
                  Create Webhook
                </Link>
              </Button>
            )}
          </>
        }
      />

      <DataTable
        tableId="manage-webhooks:list:v1"
        columns={columns}
        data={formattedData}
        isLoading={loading}
        searchPlaceholder="Search by name, event or URL…"
        emptyTitle="No webhooks yet"
        emptyDescription="Create a webhook to receive HTTP callbacks on call events."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={showActions ? { right: ['actions'] } : undefined}
        exportFilename="webhook-list.csv"
        pageSize={10}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this webhook?"
        description={
          <>
            Webhook{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.webhook_name || `#${pendingDelete?.id}`}
            </span>{' '}
            will be permanently deleted. This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />

      <Dialog open={jsonModal !== null} onOpenChange={(o) => !o && setJsonModal(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{jsonModal?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end">
            <CopyButton text={jsonModal?.content ?? ''} />
          </div>
          <pre
            className="overflow-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs"
            style={{ maxHeight: '60vh' }}
          >
            {jsonModal?.content}
          </pre>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJsonModal(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default ManageWebhookList;
